import { nothing, svg } from 'lit';
import { Legends } from '../legend/Legends.js';
import { legendsConfiguration } from '../legend/types.js';
import { highlightStateProvider, highlightYTargetStateProvider, margin, size } from '../types.js';
import { KeyScale } from '../axis/key/KeyScale.js';
import { boundsKeyAxis, boundsLeftAxis, boundsLegends, boundsPlotArea, boundsRightAxis } from '../bounds.js';
import { DomainBaseline } from '../axis/value/DomainBaseline.js';
import { KeyAxis } from '../axis/key/KeyAxis.js';
import { KeyHoverBands } from '../axis/key/KeyHoverBands.js';
import { getValueScale } from '../axis/value/getValueScale.js';
import { renderGroupsMap } from '../render-groups/types.js';
import { PointLabelCollisionManager } from '../point-label/PointLabelCollisionManager.js';
import { RenderGroups, RenderGroupsPointLabels } from '../render-groups/RenderGroups.js';
import { Supportlines } from '../axis/value/Supportlines.js';
import { ValueAxis } from '../axis/value/ValueAxis.js';
import { ValueFormatter } from '../../common/format.js';
import { OneUxPlotChartElement } from '../OneUxPlotChartElement.js';
import { DebugPointLabelCollision } from '../debug/DebugPointLabelCollision.js';
import { DebugKeyBands } from '../debug/DebugKeyBands.js';
export function ChartContent(this: OneUxPlotChartElement, {
  legendsConfiguration,
  svgSize,
  animate,
  svgMargin,
  keyScale,
  renderGroupsMap,
  valueFormatter,
  highlightStateProvider,
  highlightYTargetStateProvider
}: {
  legendsConfiguration: legendsConfiguration;
  svgSize: size;
  svgMargin: margin;
  keyScale: KeyScale;
  animate: boolean;
  renderGroupsMap: renderGroupsMap;
  valueFormatter: ValueFormatter;
  highlightStateProvider: highlightStateProvider;
  highlightYTargetStateProvider: highlightYTargetStateProvider;
}) {
  const leftValueScale = getValueScale(boundsLeftAxis(svgSize, svgMargin), renderGroupsMap.left, this.axisLeftRange);
  const rightValueScale = getValueScale(boundsRightAxis(svgSize, svgMargin), renderGroupsMap.right, this.axisRightRange);
  const defaultValueScale = (leftValueScale ?? rightValueScale)!;
  const pointLabelCollisionManager = new PointLabelCollisionManager(this.disablePointLabelCollision);
  pointLabelCollisionManager.registerCollisionRectsForValueScale(leftValueScale, 'left', valueFormatter);
  pointLabelCollisionManager.registerCollisionRectsForValueScale(rightValueScale, 'right', valueFormatter);
  return svg`
    ${this.hideLegends ? nothing : Legends({
    legendsConfiguration,
    bounds: boundsLegends(svgSize, svgMargin),
    series: this.series,
    animate,
    onLegendMouseEnter: this._onLegendMouseEnter,
    onLegendMouseLeave: this._onLegendMouseLeave,
    highlightStateProvider
  })}
      ${leftValueScale && !this.hideAxisLeft ? ValueAxis({
    valueScale: leftValueScale,
    valueFormatter,
    yTarget: 'left',
    animate,
    highlightYTargetStateProvider
  }) : nothing}
      ${rightValueScale && !this.hideAxisRight ? ValueAxis({
    valueScale: rightValueScale,
    valueFormatter,
    yTarget: 'right',
    animate,
    highlightYTargetStateProvider
  }) : nothing}
      ${!this.hideSupportlines ? Supportlines({
    bounds: boundsPlotArea(svgSize, svgMargin),
    valueScale: defaultValueScale,
    animate
  }) : nothing}
      ${RenderGroups({
    groups: renderGroupsMap.preDomain,
    keyScale,
    animate,
    leftValueScale,
    rightValueScale,
    highlightStateProvider
  })}
      ${DomainBaseline({
    bounds: boundsPlotArea(svgSize, svgMargin),
    valueScale: defaultValueScale,
    animate
  })}
      ${RenderGroups({
    groups: renderGroupsMap.postDomain,
    keyScale,
    animate,
    leftValueScale,
    rightValueScale,
    highlightStateProvider
  })}
      ${KeyAxis({
    bounds: boundsKeyAxis(svgSize, svgMargin),
    keyScale,
    animate
  })}
      ${RenderGroupsPointLabels({
    groups: renderGroupsMap.all,
    keyScale,
    valueFormatter,
    pointLabelCollisionManager,
    animate,
    leftValueScale,
    rightValueScale,
    highlightStateProvider
  })}
      ${KeyHoverBands({
    bounds: boundsPlotArea(svgSize, svgMargin),
    keyScale,
    onKeyHoverBandMouseEnter: this._onKeyHoverBandMouseEnter,
    onKeyHoverBandMouseLeave: this._onKeyHoverBandMouseLeave
  })}
      ${this.hasAttribute('debug-key-bands') ? DebugKeyBands({
    bounds: boundsPlotArea(svgSize, svgMargin),
    keyScale
  }) : nothing}
      ${this.hasAttribute('debug-point-label-collision') ? DebugPointLabelCollision(pointLabelCollisionManager) : nothing}
  `;
}